import moment from 'moment'
import {
  EnumEstadoCiclo,
  EnumEstadoFactura,
  EnumTipoCicloFacturacion,
  EnumTipoFacturacion,
  EnumTipoSujeto
} from '../../api'
import { GlobalInvoiceCycleObjectionsTableModel } from '../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import { InvoiceAnnexedParamModel } from '../../api/Argentia/endpoints/invoices/model/invoices'
import { SettlementBatchRequestModel } from '../../api/Argentia/endpoints/settlementBatchs/model/settlementBatchs'
import { SettlementTableModel } from '../../api/Argentia/endpoints/settlements/model/settlements'
import { AnnexedParameters } from '../../api/Argentia/enums'
import { EnumCriticidad } from '../../api/oData/enums/enumCriticidad'
import { EnumEstadoTarea } from '../../api/oData/enums/enumEstadoTarea'
import { EnumTipoLoteLiquidacion } from '../../api/oData/enums/enumTipoLoteLiquidacion'
import { InvoiceCodesFileUploadRequestModel } from '../../api/Pioneer/endpoints/files/models/files'
import { envConfig } from '../../config/envConfig'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import { fileToBase64 } from '../../helpers/fileToBase64'
import { sanitizeFileName } from '../../helpers/fileValidationHelper'
import i18n from '../../i18n'
import { ActionIcons, DataTableActionConfiguration } from '../../model'
import { ObjectionsModel, ObjectionTableModel } from '../invoicesPage/invoicesPage.Model'
import {
  EnumSettlementBatchMode,
  EnumSettlementSearchType,
  GlobalObjectionTableModel,
  SettlementFormSearchFilter,
  SettlementSearchRequestModel
} from './settlements.models'

export const mapFiltersToSearchRequest = (
  filters: SettlementFormSearchFilter,
  context: {
    executionCycleId: number
    invoiceCycleType: EnumTipoCicloFacturacion
    subjectType: EnumTipoSujeto
    billingType: EnumTipoFacturacion
    cycleState: EnumEstadoCiclo
    objCode?: string | null
    notIssued?: boolean
    maxResults?: number
  }
): SettlementSearchRequestModel => {
  return {
    executionCycleId: context.executionCycleId,
    invoiceCycleType: context.invoiceCycleType,
    subjectType: context.subjectType,
    billingType: context.billingType,
    cycleState: context.cycleState,
    maxResults: context.maxResults ?? envConfig.invoiceCycleConfig.maxQueryItems,
    [getSearchFieldName(filters.searchType)]: filters.searchValue,
    hasObjections: filters.hasObjections ?? (context.objCode ? true : undefined),
    objCode: context.objCode ?? undefined,
    notIssued: context.notIssued ?? undefined,
    lowQualityBad: filters.lowQualityBad,
    // Advanced filters (optional)
    hasInvoiceObjections: filters.hasInvoiceObjections,
    invoiceState: filters.invoiceState,
    amountFrom: filters.amountFrom,
    amountTo: filters.amountTo,
    issueDateFrom: filters.issueDateFrom,
    issueDateTo: filters.issueDateTo,
    subjectSubtype: filters.subjectSubtype,
    contractType: filters.contractType,
    masterObjectionId: filters.masterObjectionId
  }
}

export const mapObjectionsResponseToTableModel = (
  data: ObjectionsModel[] | undefined | null
): ObjectionTableModel[] => {
  return data?.map(item => mapObjectionResponseItemToTableModel(item)) || []
}

export const mapObjectionResponseItemToTableModel = (
  item: ObjectionsModel
): ObjectionTableModel => {
  return {
    id: item.id,
    idObjMaestra: item.idObjMaestra,
    criticalityId: item.criticalityId,
    description: item.description,
    comments: item.comments,
    invoiceID: item.invoiceID,
    documentID: item.documentID,
    settlementID: item.settlementID,
    state: item.state,
    canTerminate: item.state !== EnumEstadoTarea.Finalizada
  }
}

export const mapGlobalObjectionsResponseToTableModel = (
  data: any[] | undefined | null
): GlobalObjectionTableModel[] => {
  return data?.map(item => mapGlobalObjectionResponseItemToTableModel(item)) || []
}

export const mapGlobalObjectionResponseItemToTableModel = (
  item: GlobalInvoiceCycleObjectionsTableModel
): GlobalObjectionTableModel => {
  const actionsConfiguration = mapCriticalityToActionsConfiguration(item.criticalityId)
  return {
    id: item.id,
    idObjMaestra: item.idObjMaestra,
    criticalityId: item.criticalityId,
    description: item.description,
    comments: item.comments,
    invoiceID: 0, // No aplica para objeciones globales
    documentID: 0, // No aplica para objeciones globales
    state: item.state,
    canTerminate: item.state !== EnumEstadoTarea.Finalizada,
    type: item.type ?? [],
    executionDate: item.executionDate,
    source: item.source,
    executionCycleID: item.executionCycleID,
    settlementBatchID: item.settlementBatchID,
    actionsConfiguration,
    actions: generateIconsFromConfiguration(
      item,
      actionsConfiguration,
      new Map<ActionIcons, Function>([])
    )
  }
}

// -------------- Global Objections Icons Mappers Starts -------------- //

export const mapCriticalityToActionsConfiguration = (
  criticalityId: number
): Array<DataTableActionConfiguration> => {
  switch (criticalityId) {
    case EnumCriticidad.Grave:
      return [{ icon: ActionIcons.ERROR, tooltip: i18n.t('enums:EnumCriticidad.Grave') }]
    case EnumCriticidad.Media:
      return [{ icon: ActionIcons.WARNING, tooltip: i18n.t('enums:EnumCriticidad.Media') }]
    case EnumCriticidad.Leve:
      return [{ icon: ActionIcons.SELECTED_ITEM, tooltip: i18n.t('enums:EnumCriticidad.Leve') }]
    default:
      return []
  }
}

// -------------- Global Objections Icons Mappers Ends -------------- //

// -------------- Settlement Icons Mappers Starts -------------- //

export const mapSettlementTableApiResponse = (
  items: SettlementTableModel[]
): SettlementTableModel[] => {
  return items && items.map(mapSettlementTableServiceApiItem)
}
export const mapSettlementTableServiceApiItem = (
  item: SettlementTableModel
): SettlementTableModel => {
  return {
    ...item,
    actionsConfiguration: getGlobalActionsConfiguration()
  }
}

const getGlobalActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getObjectionsDisagreementAction()]
}

const getObjectionsDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t(
      'settlements:components.settlementsTableCard.dataTable.tooltips.objectionsError'
    )
  }
}

export const completeDataTableActions = (items: SettlementTableModel[]): SettlementTableModel[] => {
  return items.map(item => completeDataTableItemActions(item))
}

export const completeDataTableItemActions = (item: SettlementTableModel): SettlementTableModel => {
  return {
    ...item,
    actions:
      item.actionsConfiguration &&
      item.actionsConfiguration.length > 0 &&
      generateIconsFromConfiguration(
        item,
        filterApplicableActionsConfiguration(item, item.actionsConfiguration),
        new Map<ActionIcons, Function>([])
      )
  }
}

export const filterApplicableActionsConfiguration = (
  item: SettlementTableModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.ERROR: {
        if (
          item.hasObjections === true ||
          item.hasInvoiceObjections === true ||
          item.hasAnullmentInvoiceObjections === true
        ) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.LOADING: {
        applicableActionsConfiguration.push(configuration)
        break
      }
    }
  })
  return applicableActionsConfiguration
}

// -------------- Settlement Icons Mappers Ends -------------- //

export const mapFormDataToInvoiceAnnexedParam = (
  formData: any,
  invoiceId: number
): InvoiceAnnexedParamModel => ({
  idInvoiceAnnexedParam: formData.idInvoiceAnnexedParam,
  annexedParam: AnnexedParameters.COMENTARIOS,
  value: formData.comments,
  invoiceId: invoiceId,
  startDate: moment().format('YYYY-MM-DD')
})

export const mapSettlementTableServiceToModel = (
  data: SettlementTableModel[],
  isAnuladoraNueva?: boolean
): SettlementTableModel[] => {
  if (isAnuladoraNueva) {
    return data.flatMap(item => expandAnuladoraNuevaSettlement(item))
  }
  return data.map(item => mapSettlementTableServiceItemToModel(item))
}

const expandAnuladoraNuevaSettlement = (item: SettlementTableModel): SettlementTableModel[] => {
  const rows: SettlementTableModel[] = []
  // Solo crear fila anuladora si el item tiene factura anuladora
  if (item.anullmentInvoiceId) {
    rows.push({
      ...item,
      invoiceId: item.anullmentInvoiceId,
      invoiceCode: item.anullmentInvoiceCode,
      invoiceState: item.anullmentInvoiceState ?? null,
      invoiceType: item.anullmentInvoiceType ?? null,
      issuingSubject: item.anullmentIssuingSubject,
      recipientSubject: item.anullmentRecipientSubject,
      issueDate: item.anullmentIssueDate,
      amount: item.anullmentAmount ?? null,
      hasInvoiceObjections: undefined,
      hasObjections: false, // objeciones de liquidación no aplican a fila anuladora
      hasAnullmentInvoiceObjections: item.hasAnullmentInvoiceObjections,
      canManual: false,
      canRevertDiscarded: item.invoiceState === EnumEstadoFactura.Descartada,
      notIssued: item.anullmentInvoiceState !== EnumEstadoFactura.Emitida,
      isAnullmentRow: true,
      rowKey: `a_${item.id}`
    })
  }
  // Solo crear fila nueva si el item tiene factura nueva
  if (item.invoiceId) {
    rows.push({
      ...item,
      canManual: item.invoiceState === EnumEstadoFactura.Borrador,
      canRevertDiscarded: item.invoiceState === EnumEstadoFactura.Descartada,
      notIssued: item.invoiceState !== EnumEstadoFactura.Emitida,
      hasAnullmentInvoiceObjections: undefined, // no aplica a fila nueva
      isAnullmentRow: false,
      rowKey: `n_${item.id}`
    })
  }
  return rows
}

export const mapSettlementTableServiceItemToModel = (
  item: SettlementTableModel
): SettlementTableModel => {
  return {
    ...item,
    canManual: item.invoiceState === EnumEstadoFactura.Borrador,
    canRevertDiscarded: item.invoiceState === EnumEstadoFactura.Descartada,
    notIssued: item.invoiceState !== EnumEstadoFactura.Emitida
  }
}

export const mapSelectedRowsToSettlementBatchRequestModel = (
  selectedRows: SettlementTableModel[],
  invoiceIds: number[],
  type: EnumTipoLoteLiquidacion,
  executionCycleId: number,
  invoiceOptionMode: EnumSettlementBatchMode,
  formData?: any
): SettlementBatchRequestModel => {
  return {
    description: defineSettlementBatchMode(invoiceOptionMode),
    type: type,
    batchExecutionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
    startDate: formData?.startDate ? moment(formData?.startDate).format('YYYY-MM-DD') : undefined,
    endDate: formData?.endDate ? moment(formData?.endDate).format('YYYY-MM-DD') : undefined,
    cfExecutionId: executionCycleId,
    settlementIds: selectedRows?.length > 0 ? selectedRows.map(row => row.id) : [],
    invoiceIds: invoiceIds?.length > 0 ? invoiceIds : []
  }
}

const defineSettlementBatchMode = (invoiceOptionMode?: EnumSettlementBatchMode): string => {
  switch (invoiceOptionMode) {
    case EnumSettlementBatchMode.INVOICE_ALL:
      return 'FacturarTodo'
    case EnumSettlementBatchMode.INVOICE_WITHOUT_OBJECTIONS:
      return 'FacturarSinObjeciones'
    case EnumSettlementBatchMode.INVOICE_WITH_OBJECTIONS:
      return 'FacturarConObjecionesMediasLeves'
    case EnumSettlementBatchMode.INVOICE_CUSTOM_SELECTION:
      return 'FacturarSeleccionPersonalizada'
    case EnumSettlementBatchMode.ISSUE_ALL:
      return 'EmitirTodo'
    case EnumSettlementBatchMode.ISSUE_WITH_OBJECTIONS:
      return 'EmitirConObjecionesMediasLeves'
    case EnumSettlementBatchMode.ISSUE_WITH_DATE_RANGE:
      return 'EmitirConRangoFechas'
    case EnumSettlementBatchMode.ISSUE_CUSTOM_SELECTION:
      return 'EmitirSeleccionPersonalizada'
    case EnumSettlementBatchMode.ISSUE_WITHOUT_OBJECTIONS:
      return 'EmitirSinObjeciones'
    case EnumSettlementBatchMode.REGENERATE_INVOICES:
      return 'RegenerarFacturas'
    case EnumSettlementBatchMode.REJECT_INVOICES:
      return 'RechazarFacturas'
    case EnumSettlementBatchMode.ANNULATE_CUSTOM_SELECTION:
      return 'AnularSeleccionPersonalizada'
    case EnumSettlementBatchMode.REPAINT_INVOICES:
      return 'RepintarFacturas'
    case EnumSettlementBatchMode.DELETE_ERROR_SETTLEMENTS:
      return 'EliminarLiquidacionesError'
    default:
      return 'FacturarTodo'
  }
}

export const getSearchFieldName = (searchType: EnumSettlementSearchType): string => {
  const mappingFieldName: { [key in EnumSettlementSearchType]: string } = {
    [EnumSettlementSearchType.Contract]: 'contractCode',
    [EnumSettlementSearchType.CUPS]: 'cupsCode',
    [EnumSettlementSearchType.Subject]: 'subjectName',
    [EnumSettlementSearchType.Trade]: 'tradeCode',
    [EnumSettlementSearchType.Product]: 'productCode',
    [EnumSettlementSearchType.InvoiceCode]: 'invoiceCode'
  }
  return mappingFieldName[searchType]
}

export const mapInvoiceCodesFileUploadRequest = async (
  file: File,
  executionCycleId: number,
  operationType: number
): Promise<InvoiceCodesFileUploadRequestModel> => {
  const contenido = await fileToBase64(file)
  const blobName = sanitizeFileName(file.name)
  return {
    file: { blobName, contenido },
    excecutionCycleId: executionCycleId,
    operationType
  }
}
